import React, {
  ForwardedRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
} from "react";

import GridColumn from "../GridColumns/GridColumn";
import {
  ApiClient,
  GridFilterSettings,
  GridFilters,
  GridMetrics,
  PredicateModel,
  SecondaryGrid,
  SortDescriptorModel,
  useGrid,
} from "../../../";
import { BaseGridModel } from "../GridTypes/BaseGridModel";
import { GridComponent } from "@syncfusion/ej2-react-grids";
import GridHeightSettings from "../GridSettings/GridHeightSettings";
import { Predicate } from "@syncfusion/ej2-data";

export interface PrimaryGridTableModule {
  /** Resets the form to its initial state. */
  getGridRef: () => GridComponent | null;
}

interface PrimaryGridTableComponentProps<
  TGridModel extends object,
  TMetricsModel
> {
  gridKey: string;
  gridViewTitle?: string;
  columnsConfiguration?: Readonly<GridColumn[]>;
  filterConfiguration?: PredicateModel[];
  sortConfiguration?: SortDescriptorModel[];
  /* eslint-disable no-unused-vars */
  gridConfiguration?: Readonly<BaseGridModel<TGridModel>>;
  onFilterSettingsChanged: (settings?: GridFilterSettings) => void;
  odataUrl: string;
  odataParams?: Record<string, string>;
  getAuthRequestHeader: () => Promise<Record<string, string>>;
  metricsSettings?: {
    url: string;
    columns?: number;
    apiClient: ApiClient;
    languageMapping: TMetricsModel;
    showMetricViewPicker?: boolean;
    metricConfiguration: string[];
    updateViewMetricSettings: (
      viewId: string,
      metricSettings: string[]
    ) => Promise<void>;
    viewId?: string;
  };
  showGridFilters?: boolean;
  allowSearch?: boolean;
  predicate?: Predicate;
  gridHeightSettings?: GridHeightSettings;
}

// Module augmentations allow you to extend the types of existing modules.
// https://www.digitalocean.com/community/tutorials/typescript-module-augmentation#enter-module-augmentation
declare module "react" {
  function forwardRef<T, P = {}>(
    render: (props: P, ref: React.Ref<T>) => React.ReactElement | null
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null;
}

const PrimaryGridTableComponent = forwardRef(
  <TGridModel extends object, TMetricsModel extends object>(
    props: PrimaryGridTableComponentProps<TGridModel, TMetricsModel>,
    ref: ForwardedRef<PrimaryGridTableModule>
  ) => {
    useImperativeHandle(
      ref,
      () =>
        ({
          getGridRef: () => {
            return gridRef.current;
          },
        } as PrimaryGridTableModule)
    );

    const {
      ready,
      gridRef,
      gridSettings,
      filtersCount,
      filterSettings,
      removeFilter,
      clearFilters,
      removeSort,
    } = useGrid<TGridModel>({
      gridKey: props.gridKey,
      gridViewTitle: props.gridViewTitle,
      columnsConfiguration: props.columnsConfiguration || [],
      filterConfiguration: props.filterConfiguration,
      sortConfiguration: props.sortConfiguration,
      dataSource: props.odataUrl,
      dataParams: props.odataParams,
      getAuthRequestHeader: props.getAuthRequestHeader,
      gridConfiguration: props.gridConfiguration,
      allowSearch: props.allowSearch,
      predicate: props.predicate,
      gridHeightSettings: props.gridHeightSettings,
    });

    useEffect(() => {
      props.onFilterSettingsChanged &&
        props.onFilterSettingsChanged(
          filterSettings ? { ...filterSettings } : undefined
        );
    }, [filterSettings]);

    return (
      <div className="ucl-main-grid-views-picker-grid">
        {props.showGridFilters && (
          <GridFilters
            gridKey={props.gridKey}
            filtersCount={filtersCount}
            filterSettings={filterSettings}
            removeFilter={removeFilter}
            clearFilters={clearFilters}
            removeSort={removeSort}
          />
        )}
        {gridRef && (
          <SecondaryGrid
            ready={ready}
            gridRef={gridRef as React.RefObject<GridComponent>}
            gridSettings={gridSettings}
            className="ucl-main-grid"
          />
        )}
        {props.metricsSettings && (
          <GridMetrics<TMetricsModel>
            gridKey={props.gridKey}
            gridViewTitle={props.gridViewTitle}
            viewId={props.metricsSettings.viewId}
            url={props.metricsSettings.url}
            columns={props.metricsSettings.columns}
            minimal={true}
            languageMapping={props.metricsSettings.languageMapping}
            apiClient={props.metricsSettings.apiClient}
            getAuthRequestHeader={props.getAuthRequestHeader}
            filterSettings={filterSettings}
            metricConfiguration={props.metricsSettings.metricConfiguration}
            showMetricViewPicker={props.metricsSettings.showMetricViewPicker}
            updateViewMetricSettings={
              props.metricsSettings.updateViewMetricSettings
            }
          />
        )}
      </div>
    );
  }
);

export const PrimaryGridTable = PrimaryGridTableComponent;
export default PrimaryGridTable;
